// Data Lab · Person record — Health strip + Controls + CRM/AI/Notes zones.
// Renders the Module 06 §3-§7 enhancements. Read-only: normalises a COPY of the
// doc at render time, never writes to Mongo.
import { useMemo, useState } from "react";
import {
  ShieldCheck, ShieldAlert, RefreshCw, Loader2, StickyNote, Sparkles,
  Building2, AlertTriangle, Info, ChevronDown, ChevronRight, Pencil, Save, X,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { personHealth, isEmpty } from "./_normalise";
import { CRM_ENRICHMENT_FIELDS, PROV_TONE, PROV_LABEL } from "../../config/datalabProvenance";
import { dateGB } from "./_shared";

const SEV = {
  high: { color: "#fca5a5", bg: "rgba(244,63,94,0.12)", border: "rgba(244,63,94,0.4)", label: "HIGH", Icon: ShieldAlert },
  med:  { color: "#fcd34d", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)", label: "MED", Icon: AlertTriangle },
  info: { color: "#5eead4", bg: "rgba(45,212,191,0.12)", border: "rgba(45,212,191,0.35)", label: "INFO", Icon: Info },
  low:  { color: "#94a3b8", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.30)", label: "LOW", Icon: Info },
};

const BAR_META = {
  bc: { label: "Contact & aggregates", color: "#5eead4" },
  crm: { label: "CRM fields", color: "#e0a94f" },
  sys: { label: "System linkage", color: "#94a3b8" },
  "crm-enrich": { label: "360 enrichment targets", color: "#c4b5fd" },
};

function ProvBadge({ cls }) {
  const t = PROV_TONE[cls] || PROV_TONE.sys;
  return (
    <span className="font-mono" style={{
      fontSize: 8.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase",
      padding: "1px 5px", borderRadius: 4, color: t.color, background: t.bg, border: `1px solid ${t.border}`,
    }}>{PROV_LABEL[cls] || cls}</span>
  );
}

const NOTE_TYPE_TONE = {
  order: { color: "#5eead4", bg: "rgba(45,212,191,0.12)" },
  email: { color: "#c4b5fd", bg: "rgba(167,139,250,0.12)" },
  "email summary": { color: "#c4b5fd", bg: "rgba(167,139,250,0.12)" },
};

function noteTone(type) {
  return NOTE_TYPE_TONE[String(type || "").toLowerCase()] || { color: "#94a3b8", bg: "rgba(148,163,184,0.1)" };
}

function Zone({ icon: Icon, title, right, children, testid }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-white/10 bg-[#0F1420]" data-testid={testid}>
      <div role="button" tabIndex={0} onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter") setOpen((v) => !v); }}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left cursor-pointer select-none">
        <span className="flex items-center gap-2 text-slate-200 text-sm font-semibold">
          {Icon && <Icon className="w-4 h-4 text-[#C8A46B]" />}{title}
        </span>
        <span className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
          {right}{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </div>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function GapField({ label, value }) {
  const empty = isEmpty(value);
  return (
    <div className="grid grid-cols-[150px_1fr] gap-3 py-1.5 border-t border-white/[0.05] items-center"
      style={empty ? { background: "linear-gradient(90deg, rgba(224,169,79,.06), transparent)" } : undefined}>
      <div className="text-[11px] font-mono text-slate-500">{label}</div>
      {empty ? (
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-amber-300/90 font-mono">Needs enrichment</span>
          <span className="text-[8.5px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300/80">gap</span>
        </div>
      ) : (
        <div className="text-[13px] text-slate-100 break-words">{String(value)}</div>
      )}
    </div>
  );
}

export default function PersonHealthZones({ doc, onRefresh, refreshing }) {
  const [hideEmpty, setHideEmpty] = useState(true);
  const [filter, setFilter] = useState("all"); // all | crm | contact | system
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const health = useMemo(() => personHealth(doc || {}), [doc]);

  const startEdit = () => {
    const f = {};
    CRM_ENRICHMENT_FIELDS.forEach((x) => { f[x.key] = doc[x.key] ?? ""; });
    setForm(f); setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/datalab/persons/${doc._id}/enrich`, { fields: form });
      toast.success(data.updated > 0 ? `Saved ${data.updated} field(s) · logged to audit` : "No changes to save");
      setEditing(false);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const showContactZone = ["all", "crm", "contact"].includes(filter);
  const showContactFields = filter === "all" || filter === "contact";
  const showCrmGaps = filter === "all" || filter === "crm";
  const showAi = filter === "all" || filter === "crm";
  const showNotes = filter === "all" || filter === "crm";
  const gradeWarn = health.grade === "NEEDS ATTENTION";

  const contactFields = [
    ["Email", doc.Email], ["Phone", doc.Phone], ["Company", doc.Company],
    ["Primary Address", [doc.Address1, doc.City, doc.PostCode].filter(Boolean).join(", ")],
  ].filter(([, v]) => showContactFields && (!hideEmpty || !isEmpty(v)));

  const emptyHiddenCount = [doc.Email, doc.Phone, doc.Company].filter((v) => hideEmpty && isEmpty(v)).length;

  return (
    <div className="space-y-4" data-testid="person-health-zones">
      {/* Data Health strip */}
      <div className="rounded-xl border p-4" data-testid="person-data-health"
        style={{ borderColor: gradeWarn ? "rgba(244,63,94,0.3)" : "rgba(16,185,129,0.3)", background: "#0F1420" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            {gradeWarn ? <ShieldAlert className="w-4 h-4 text-rose-400" /> : <ShieldCheck className="w-4 h-4 text-emerald-400" />}
            <span className="text-[10px] uppercase tracking-widest font-mono text-[#C8A46B]">Data Health</span>
            <span className="font-mono font-bold text-[11px] px-2 py-0.5 rounded"
              data-testid="person-health-grade"
              style={{
                color: gradeWarn ? "#fca5a5" : "#6ee7b7",
                background: gradeWarn ? "rgba(244,63,94,0.12)" : "rgba(16,185,129,0.12)",
                border: `1px solid ${gradeWarn ? "rgba(244,63,94,0.4)" : "rgba(16,185,129,0.4)"}`,
              }}>{health.grade}</span>
            <span className="text-[11px] font-mono text-slate-500">{health.issues.length} issue{health.issues.length === 1 ? "" : "s"}</span>
          </div>
          <button onClick={onRefresh} disabled={refreshing} data-testid="person-refresh-live"
            className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-50">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh live
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* issues */}
          <div className="space-y-1.5">
            {health.issues.length === 0 && <div className="text-[12px] text-emerald-300/80">No issues detected.</div>}
            {health.issues.map((it, i) => {
              const s = SEV[it.sev] || SEV.low;
              return (
                <div key={i} className="rounded-lg px-2.5 py-1.5" style={{ borderLeft: `2px solid ${s.border}`, background: "rgba(255,255,255,0.02)" }} data-testid={`person-health-issue-${it.sev}`}>
                  <div className="flex items-start gap-2">
                    <s.Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: s.color }} />
                    <div className="min-w-0">
                      <div className="text-[12px] text-slate-200">{it.text}
                        <span className="ml-1.5 font-mono" style={{ fontSize: 8.5, fontWeight: 800, color: s.color, background: s.bg, border: `1px solid ${s.border}`, padding: "0 5px", borderRadius: 4 }}>{s.label}</span>
                      </div>
                      <div className="text-[10px] font-mono text-slate-500 mt-0.5">{it.route}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* completeness bars */}
          <div className="space-y-2">
            {health.bars.map((b) => {
              const m = BAR_META[b.cls] || { label: b.cls, color: "#94a3b8" };
              return (
                <div key={b.cls}>
                  <div className="flex items-center justify-between text-[10.5px] font-mono text-slate-400 mb-0.5">
                    <span>{m.label}</span>
                    <span className="tabular-nums text-slate-500">{b.filled}/{b.total} · {b.pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${Math.max(2, b.pct)}%`, background: m.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="person-controls">
        <button onClick={() => setHideEmpty((v) => !v)} data-testid="person-hide-empty"
          className={`inline-flex items-center gap-2 text-[11px] font-mono px-2.5 py-1.5 rounded-lg border ${hideEmpty ? "border-teal-500/40 bg-teal-500/10 text-teal-200" : "border-white/10 text-slate-400"}`}>
          Hide empty {hideEmpty ? "✓" : ""}{hideEmpty && emptyHiddenCount ? ` · ${emptyHiddenCount}` : ""}
        </button>
        <div className="flex items-center gap-1.5">
          {[["all", "All"], ["crm", "CRM gaps"], ["contact", "Contact"], ["system", "System"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`person-filter-${k}`}
              className={`text-[11px] font-mono px-2.5 py-1.5 rounded-lg border ${filter === k ? "border-[#C8A46B]/50 bg-[#C8A46B]/10 text-[#C8A46B]" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Contact & Company + CRM gaps */}
        {showContactZone && (
          <Zone icon={Building2} title="Contact & Company" right={
            showCrmGaps ? (
              editing ? (
                <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <button onClick={saveEdit} disabled={saving} data-testid="person-enrich-save"
                    className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/15 text-emerald-200 disabled:opacity-50">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </button>
                  <button onClick={() => setEditing(false)} data-testid="person-enrich-cancel"
                    className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-white/15 text-slate-400"><X className="w-3 h-3" /></button>
                </span>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); startEdit(); }} data-testid="person-enrich-edit"
                  className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-[#C8A46B]/40 text-[#C8A46B] hover:bg-[#C8A46B]/10">
                  <Pencil className="w-3 h-3" /> Enrich
                </button>
              )
            ) : `${health.crmPct}% CRM`
          } testid="person-zone-crm">
            {showContactFields && contactFields.map(([k, v]) => (
              <div key={k} className="grid grid-cols-[150px_1fr] gap-3 py-1.5 border-t border-white/[0.05] items-center">
                <div className="text-[11px] font-mono text-slate-500">{k}</div>
                <div className="text-[13px] text-slate-100 break-words">{isEmpty(v) ? "—" : String(v)}</div>
              </div>
            ))}
            {showCrmGaps && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mt-3 mb-0.5">CRM enrichment fields</div>
                {CRM_ENRICHMENT_FIELDS.map((f) => (
                  editing ? (
                    <div key={f.key} className="grid grid-cols-[150px_1fr] gap-3 py-1 items-center border-t border-white/[0.05]">
                      <div className="text-[11px] font-mono text-slate-400">{f.label}</div>
                      <input value={form[f.key] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                        data-testid={`person-enrich-input-${f.key}`}
                        placeholder="add…"
                        className="text-[12px] bg-black/30 border border-white/10 rounded px-2 py-1 text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#C8A46B]/40" />
                    </div>
                  ) : <GapField key={f.key} label={f.label} value={doc[f.key]} />
                ))}
                {editing && <div className="text-[10px] font-mono text-slate-500 mt-2">Saving writes to the record and logs a `_enrichment_audit` entry (who / when / from→to).</div>}
              </>
            )}
          </Zone>
        )}

        {/* AI Enrichment & 360 targets */}
        {showAi && (
          <Zone icon={Sparkles} title="AI Enrichment & 360 Targets" right={health.aiPresent ? "present" : "absent"} testid="person-zone-ai">
            <div className="rounded-lg border border-dashed p-3 mb-3" style={{ borderColor: "#443c6e" }}>
              <div className="text-[13px] font-semibold" style={{ color: health.aiPresent ? "#6ee7b7" : "#c4b5fd" }}>
                {health.aiPresent ? "AI enrichment present" : "No _ai_person field on this record"}
              </div>
              <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                {health.aiPresent ? "Enrichment scaffold detected." : "Enrichment scaffold not built yet — these are the fields destined for the Customer 360 brain."}
              </div>
            </div>
            {CRM_ENRICHMENT_FIELDS.map((f) => {
              const ok = !isEmpty(doc[f.key]);
              return (
                <div key={f.key} className="flex items-center justify-between py-1 border-t border-white/[0.05]">
                  <span className="flex items-center gap-2 text-[12px] text-slate-300">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: ok ? "#34d399" : "#f87171" }} />{f.label}
                  </span>
                  <span className="text-[10.5px] font-mono text-slate-500">{ok ? "enriched" : "not enriched"}</span>
                </div>
              );
            })}
          </Zone>
        )}
      </div>

      {filter === "system" && (
        <div className="rounded-xl border border-white/10 bg-[#0F1420] p-4 text-[12px] font-mono text-slate-400" data-testid="person-system-pointer">
          System linkage — Pipedrive/BC IDs, BC Cust IDs and Order ID chips — are shown in the panels below ↓
        </div>
      )}

      {/* Notes — deduped, HTML, tagged, contamination-flagged */}
      {showNotes && (
        <Zone icon={StickyNote} title="Intelligence · Notes"
          right={`${health.uniqueNotes.length} unique${health.contaminatedKeys.size ? ` · ${health.contaminatedKeys.size} flagged` : ""}`}
          testid="person-zone-notes">
          {health.uniqueNotes.length === 0 && <div className="text-[12px] text-slate-500">No notes on this record.</div>}
          <div className="space-y-2.5">
            {health.uniqueNotes.map((n, i) => {
              const bad = health.contaminatedKeys.has(n.content);
              const t = noteTone(n.type);
              return (
                <div key={i} className="rounded-lg border p-3"
                  style={{ borderColor: bad ? "rgba(244,63,94,0.4)" : "var(--t2-border, rgba(255,255,255,0.08))", background: "rgba(255,255,255,0.02)" }}
                  data-testid={`person-note-${i}`}>
                  {bad && (
                    <div className="flex items-center gap-1.5 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1 mb-2" data-testid={`person-note-contaminated-${i}`}>
                      <AlertTriangle className="w-3.5 h-3.5" /> This note appears to belong to a different customer
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="font-mono uppercase" style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: ".06em", color: t.color, background: t.bg, padding: "1px 6px", borderRadius: 4 }}>{n.type}</span>
                    {n.seen > 1 && <span className="font-mono text-[9px] text-slate-500 px-1.5 py-0.5 rounded border border-white/10">seen {n.seen}×</span>}
                    {n.date && <span className="font-mono text-[10px] text-slate-500 ml-auto">{dateGB(n.date) || n.date}</span>}
                  </div>
                  <div className="text-[12.5px] text-slate-300 leading-relaxed lab-note-body"
                    dangerouslySetInnerHTML={{ __html: n.content }} />
                </div>
              );
            })}
          </div>
        </Zone>
      )}
    </div>
  );
}
